import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {Validators} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatChipInputEvent} from '@angular/material/chips';
import {SubscriptSizing} from '@angular/material/form-field';
import {equals} from '@aztrix/helpers';
import {Property} from '@aztrix/models';
import {FormControl, FormGroup} from '@ngneat/reactive-forms';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {map, startWith} from 'rxjs/operators';

@Component({
  selector: 'ax-chips-edit',
  templateUrl: './chips-edit.component.html',
  styleUrls: ['chips-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup<any>;
  @Input() label?: string;
  @Input() hint?: string;
  @Input() required = false;
  @Input() autocompleteValues: string[];
  @Input() disableSpecialFileCharacters = false;
  @Input() errorsTemplate: TemplateRef<unknown>;
  @Input() readonly = false;
  @Input() subscriptSizing: SubscriptSizing = 'dynamic';

  separatorKeysCodes: number[] = [ENTER, COMMA];
  chipsCtrl = new FormControl<string | null>();
  filteredChips$: Observable<string[]>;
  chips: string[] = [];
  chips$ = new BehaviorSubject<string[]>([]);

  private _subscriptions = new Subscription();
  private _formSubscription = new Subscription();

  @ViewChild('chipGrid') chipList: any;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(private _changeDetector: ChangeDetectorRef) {
    this.filteredChips$ = combineLatest([
      this.chipsCtrl.valueChanges.pipe(startWith(this.chipsCtrl.value)),
      this.chips$,
    ]).pipe(
      map(
        ([value]) =>
          this.autocompleteValues?.filter(
            (chip) =>
              (!value || chip.toLowerCase().indexOf(value?.toLowerCase()) === 0) &&
              !this._duplicate(chip)
          )
      )
    );
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this.form.touch$.subscribe((touched) => {
        if (touched) {
          this.chipsCtrl.markAsTouched();
        } else {
          this.chipsCtrl.markAsUntouched();
        }
        this._changeDetector.markForCheck();
      })
    );

    this._subscriptions.add(
      this.chipsCtrl.touch$.subscribe((touched) => {
        if (touched) {
          this.form.markAllAsTouched();
        } else {
          this.form.markAllAsTouched();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.required) {
      if (this.required) {
        this.chipsCtrl.setValidators(Validators.required);
      } else {
        this.chipsCtrl.setValidators([]);
      }
    }

    if (changes.form && this.form) {
      this._formSubscription.unsubscribe();
      this._formSubscription = this.form.valueChanges
        .pipe(startWith(this.form.value))
        .subscribe((property: Property) => {
          const values = property?.value?.split(',').filter((v) => v?.length);
          if (!equals(this.chips$.value, values) && values) {
            this.chips = values;
            this.chips$.next(values);
          }
        });

      this._formSubscription.add(
        this.form.statusChanges.subscribe(() => {
          this._setFormState();
        })
      );
    }
  }

  ngOnDestroy(): void {
    if (!this._formSubscription.closed) {
      this._formSubscription.unsubscribe();
    }
    if (!this._subscriptions.closed) {
      this._subscriptions.unsubscribe();
    }
  }

  add({chipInput, value}: MatChipInputEvent): void {
    value = (value || '').trim();

    if (value && !this._duplicate(value)) {
      this.chips.push(value);
      this.chips$.next(this.chips);
    }

    if (chipInput) {
      chipInput.inputElement.value = '';
    }

    this.chipsCtrl.setValue(null);
    this._setFormValue();
  }

  remove(value: string): void {
    const index = this.chips.indexOf(value);

    if (index >= 0) {
      this.chips.splice(index, 1);
      this.chips$.next(this.chips);
    }

    this._setFormState();
    this._setFormValue();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (!this._duplicate(event.option.viewValue)) {
      this.chips.push(event.option.viewValue.trim());
      this.chips$.next(this.chips);
    }
    this.chipsCtrl.setValue(null);

    this._setFormState();
    this._setFormValue();
  }

  clearValue() {
    this.chips = [];
    this.chips$.next(this.chips);
    this.form.reset();
    this.form.markAsDirty();
  }

  private _setFormValue() {
    const value = this.chips$.value?.join(',');
    this.form.setValue({...this.form.value, value});
    this.form.markAsDirty();
  }

  private _setFormState() {
    this.chipList.errorState = this.form.invalid;
    this.chipsCtrl.updateValueAndValidity();
  }

  private _duplicate(valueToCheck: string): boolean {
    return (
      this.chips
        .map((chip) => chip.toLowerCase())
        .findIndex((chip) => valueToCheck.trim().toLowerCase() === chip) >= 0
    );
  }

  get hasValue() {
    return this.form?.value?.value;
  }
}
